/**
 * NAO — Historia del producto: driver del teatro scroll-driven.
 *
 * Dos modos sobre el mismo markup (sections/nao-story-3d.liquid):
 *  - Storyboard 2D (hoy): crossfade de fotos por capítulo.
 *  - 3D (cuando exista el GLB): <model-viewer> lazy; la animación única del
 *    GLB se scrubbea con el scroll (currentTime = progreso × duración), la
 *    cámara interpola por capítulo y el tramo final habilita órbita libre,
 *    hotspots y AR. Spec del asset: Docs/NAO_Modelo_3D_Spec_2026-07-15.md.
 *
 * Presupuesto de rendimiento: un solo listener de scroll rAF-gated activo
 * únicamente mientras la sección está cerca del viewport; el 3D (librería y
 * GLB) no se descarga hasta estar a ~1.5 viewports. Con
 * prefers-reduced-motion no hay teatro ni scrub: contenido apilado y visor
 * interactivo directo si hay modelo.
 */

// Autohospedable: descargar este archivo a assets/ y cambiar la constante
// (ver Docs/NAO_Modelo_3D_Spec_2026-07-15.md §7).
const MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let modelViewerLibPromise = null;

function loadModelViewerLib() {
    if (!modelViewerLibPromise) {
        modelViewerLibPromise = import(MODEL_VIEWER_SRC);
    }
    return modelViewerLibPromise;
}

function clamp01(value) {
    return Math.min(Math.max(value, 0), 1);
}

function initStory(root) {
    (root || document).querySelectorAll('[data-nao-story]').forEach((story) => {
        if (story.dataset.naoStoryInit) return;
        story.dataset.naoStoryInit = 'true';

        const track = story.querySelector('[data-nao-story-track]');
        const canvas = story.querySelector('[data-nao-story-canvas]');
        const panels = Array.from(story.querySelectorAll('[data-nao-story-panel]'));
        const frames = Array.from(story.querySelectorAll('[data-nao-story-frame]'));
        const finalOverlay = story.querySelector('[data-nao-story-final]');
        const progressBar = story.querySelector('[data-nao-story-bar]');
        const viewerTemplate = story.querySelector('[data-nao-story-viewer-template]');
        const arButton = story.querySelector('[data-nao-story-ar]');

        if (!track || panels.length === 0) return;

        const chapters = panels.map((panel) => ({
            panel,
            start: clamp01(parseFloat(panel.dataset.start) || 0),
            end: clamp01(parseFloat(panel.dataset.end) || 1),
            cameraOrbit: panel.dataset.cameraOrbit || '',
        }));

        const hasModel = Boolean(story.dataset.modelUrl && viewerTemplate);
        const video = story.querySelector('[data-nao-story-video]');
        const hasVideo = Boolean(video);
        let viewer = null;
        let viewerReady = false;
        let videoReady = false;
        let activeChapter = -1;
        let finalShown = false;

        // ------- Video-scrub: seeks serializados -------
        // Un seek nuevo solo se emite cuando terminó el anterior; si llegan
        // más durante el seek, se recuerda solo el último (coalescing). Así
        // el video nunca se atraganta en scroll rápido. El watchdog libera
        // el candado si `seeked` nunca llega (decoders Android lentos o
        // eventos perdidos): sin él, un solo seek colgado congelaba el
        // scrub para siempre.
        let videoSeeking = false;
        let videoPendingTime = null;
        let videoSeekWatchdog = null;

        function releaseSeek() {
            videoSeeking = false;
            if (videoPendingTime !== null) {
                const next = videoPendingTime;
                videoPendingTime = null;
                seekVideo(next);
            }
        }

        function seekVideo(time) {
            if (videoSeeking) {
                videoPendingTime = time;
                return;
            }
            videoSeeking = true;
            clearTimeout(videoSeekWatchdog);
            videoSeekWatchdog = setTimeout(releaseSeek, 750);
            video.currentTime = time;
        }

        if (hasVideo) {
            video.addEventListener('seeked', () => {
                clearTimeout(videoSeekWatchdog);
                releaseSeek();
            });
        }

        // ------- Modo reduced-motion: sin teatro; visor interactivo directo -------
        if (reducedMotion.matches) {
            if (finalOverlay) finalOverlay.hidden = false;
            if (hasModel) {
                const io = new IntersectionObserver((entries, observer) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    observer.disconnect();
                    mountViewer(true);
                }, { rootMargin: '50%' });
                io.observe(track);
            }
            return;
        }

        story.classList.add('nao-story--enhanced');
        if (finalOverlay) finalOverlay.hidden = false;

        // ------- Modo video: el scroll scrubbea el video (efecto 3D) -------
        let videoKicked = false;
        if (hasVideo) {
            const activateVideo = () => {
                if (videoReady) return;
                videoReady = true;
                story.classList.add('nao-story--video');
                // El video manda: las fotos del storyboard se apagan.
                frames.forEach((frame) => frame.classList.remove('is-active'));
                requestUpdate();
            };
            if (video.readyState >= 2) {
                activateVideo();
            } else {
                video.addEventListener('loadeddata', activateVideo, { once: true });
                video.addEventListener('canplay', activateVideo, { once: true });
            }
        }

        // En móvil (iOS / Android con ahorro de datos) cambiar `preload` no
        // descarga nada: sin load() explícito y un primer seek tras los
        // metadatos, loadeddata nunca llega, el modo video no se activa y
        // solo se ven las fotos de fallback (bug real: el video solo se
        // veía en desktop). El video es muted+playsinline y nunca se
        // reproduce, así que load()/seek no chocan con las políticas de
        // autoplay.
        function kickVideoLoad() {
            if (!hasVideo || videoKicked) return;
            videoKicked = true;
            video.preload = 'auto';
            video.addEventListener(
                'loadedmetadata',
                () => {
                    if (!videoReady && !videoSeeking && video.currentTime === 0) {
                        seekVideo(0.001);
                    }
                },
                { once: true }
            );
            try {
                video.load();
            } catch (error) {
                // load() puede lanzar en navegadores antiguos: el preload
                // ya quedó en auto y el fallback de fotos sigue en pie.
            }
        }

        // ------- Visor 3D -------
        function mountViewer(interactive) {
            if (viewer || !hasModel) return;
            loadModelViewerLib()
                .then(() => {
                    if (viewer) return;
                    const fragment = viewerTemplate.content.cloneNode(true);
                    viewer = fragment.querySelector('model-viewer');
                    if (!viewer) return;

                    if (interactive) viewer.setAttribute('camera-controls', '');

                    viewer.addEventListener('load', () => {
                        viewerReady = true;
                        const animations = viewer.availableAnimations || [];
                        if (animations.length > 0) {
                            viewer.animationName = animations[0];
                            viewer.pause();
                            viewer.currentTime = 0;
                        }
                        // El modelo manda: las fotos del storyboard se apagan.
                        frames.forEach((frame) => frame.classList.remove('is-active'));
                        story.classList.add('nao-story--3d');

                        if (arButton && viewer.canActivateAR) {
                            arButton.hidden = false;
                            arButton.addEventListener('click', () => viewer.activateAR());
                        }
                    });

                    canvas.appendChild(viewer);
                })
                .catch(() => {
                    // Sin red o CDN caído: el storyboard 2D sigue siendo la experiencia.
                });
        }

        // ------- Scrub por scroll -------
        let raf = null;
        let listening = false;

        function currentProgress() {
            const rect = track.getBoundingClientRect();
            const total = rect.height - window.innerHeight;
            if (total <= 0) return 0;
            return clamp01(-rect.top / total);
        }

        function setChapter(index) {
            if (index === activeChapter) return;
            activeChapter = index;

            chapters.forEach((chapter, i) => {
                chapter.panel.classList.toggle('is-active', i === index);
            });
            frames.forEach((frame, i) => {
                if (
                    !story.classList.contains('nao-story--3d') &&
                    !story.classList.contains('nao-story--video')
                ) {
                    frame.classList.toggle('is-active', i === index);
                }
            });

            const chapter = chapters[index];
            if (viewer && viewerReady && chapter && chapter.cameraOrbit) {
                viewer.setAttribute('camera-orbit', chapter.cameraOrbit);
            }

            if (viewer) {
                viewer.querySelectorAll('[data-nao-story-hotspot]').forEach((hotspot) => {
                    const forChapter = parseInt(hotspot.dataset.naoStoryHotspot, 10);
                    if (forChapter === index) {
                        hotspot.setAttribute('data-nao-hotspot-visible', '');
                    } else {
                        hotspot.removeAttribute('data-nao-hotspot-visible');
                    }
                });
            }
        }

        function setFinal(show) {
            if (show === finalShown || !finalOverlay) return;
            finalShown = show;
            finalOverlay.classList.toggle('is-visible', show);
            // Tramo final: el producto pasa a ser del usuario (órbita libre).
            if (viewer && viewerReady) {
                if (show) {
                    viewer.setAttribute('camera-controls', '');
                    viewer.removeAttribute('disable-zoom');
                } else {
                    viewer.removeAttribute('camera-controls');
                    viewer.setAttribute('disable-zoom', '');
                }
            }
        }

        function update() {
            raf = null;
            const progress = currentProgress();

            if (progressBar) {
                progressBar.style.transform = `scaleY(${progress.toFixed(4)})`;
            }

            let index = 0;
            for (let i = 0; i < chapters.length; i++) {
                if (progress >= chapters[i].start) index = i;
            }
            setChapter(index);
            setFinal(progress >= chapters[chapters.length - 1].start);

            if (viewer && viewerReady && typeof viewer.duration === 'number' && viewer.duration > 0) {
                viewer.currentTime = progress * viewer.duration;
            }

            if (videoReady && video.duration > 0) {
                // Margen de 40ms al final: buscar el último frame exacto
                // produce negro/parpadeo en algunos navegadores.
                const target = progress * Math.max(video.duration - 0.04, 0);
                if (Math.abs(video.currentTime - target) > 0.001) {
                    seekVideo(target);
                }
            }
        }

        function requestUpdate() {
            if (raf === null) raf = requestAnimationFrame(update);
        }

        function listen(active) {
            if (active === listening) return;
            listening = active;
            if (active) {
                window.addEventListener('scroll', requestUpdate, { passive: true });
                window.addEventListener('resize', requestUpdate, { passive: true });
                requestUpdate();
            } else {
                window.removeEventListener('scroll', requestUpdate);
                window.removeEventListener('resize', requestUpdate);
            }
        }

        // Escuchar scroll solo cerca de la sección; precargar el asset pesado
        // (video o 3D) a ~1.5 viewports. El video tiene prioridad sobre el
        // GLB mientras exista: es la experiencia "3D" actual.
        const nearObserver = new IntersectionObserver(
            (entries) => {
                const near = entries.some((entry) => entry.isIntersecting);
                listen(near);
                if (near) {
                    if (hasVideo) {
                        kickVideoLoad();
                    } else {
                        mountViewer(false);
                    }
                }
            },
            { rootMargin: '150% 0%' }
        );
        nearObserver.observe(track);

        requestUpdate();
    });
}

initStory(document);
document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initStory(event.target);
});
